import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { FadeUp, Eyebrow } from "@/components/motion";
import Seo from "@/components/Seo";
import NewsletterForm from "@/components/NewsletterForm";
import { fmtDate } from "@/pages/News";

export default function NewsPost() {
  const { slug } = useParams();
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ["post", slug],
    queryFn: async () => (await api.get(`/posts/${slug}`)).data,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-24 font-mono text-xs uppercase tracking-[0.3em] text-mute2">
        Opening the notebook…
      </div>
    );
  }
  if (isError || !post) {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-24 text-center">
        <p className="font-serif text-4xl font-light text-ink">This note slipped out of the notebook.</p>
        <Link to="/news" className="font-mono text-[11px] uppercase tracking-[0.24em] text-cyanx" data-testid="post-back-news-link">
          Back to News &amp; notes
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1440px] px-5 pb-28 pt-32 sm:px-8 lg:px-14 lg:pt-40">
      <Seo title={`${post.title} | Isabella MacLeod`} description={post.excerpt} path={`/news/${post.slug}`} />

      <Link
        to="/news"
        data-testid="post-back-link"
        className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.24em] text-mute2 transition-colors hover:text-cyanx"
      >
        <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" />
        All notes
      </Link>

      <article className="mx-auto mt-12 max-w-3xl">
        <FadeUp>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <Eyebrow>{fmtDate(post.published_at)}</Eyebrow>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ember">{post.tag}</span>
          </div>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {post.title}
          </h1>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-10">
          <div className="space-y-6">
            {post.body.split("\n\n").map((para, i) => (
              <p key={i} className="text-lg font-light leading-relaxed text-dim">
                {para}
              </p>
            ))}
          </div>
        </FadeUp>

        {post.link_url && (
          <FadeUp delay={0.15} className="mt-10">
            <a
              href={post.link_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="post-external-link"
              className="group inline-flex items-center gap-2 rounded-full bg-cyanx px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-void transition-all duration-300 hover:brightness-110"
            >
              {post.link_label || "Read more"}
              <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </FadeUp>
        )}
      </article>

      <FadeUp className="mx-auto mt-16 max-w-3xl border-t border-white/[0.07] pt-10">
        <Eyebrow className="mb-3">Enjoying the notes?</Eyebrow>
        <h2 className="font-serif text-3xl font-light text-ink">
          Get them by <span className="serif-italic text-cyanx">email</span>
        </h2>
        <p className="mt-3 max-w-lg text-sm font-light leading-relaxed text-dim">
          One email when there's something worth reading — book news, essays,
          and behind-the-book notes. Double opt-in, unsubscribe anytime.
        </p>
        <div className="mt-7 max-w-xl" data-testid="post-newsletter-form">
          <NewsletterForm compact />
        </div>
        <Link
          to="/contact"
          data-testid="post-contact-link"
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mute2 transition-colors hover:text-cyanx"
        >
          Or write to Isabella directly
          <ArrowRight size={12} />
        </Link>
      </FadeUp>
    </div>
  );
}
